/**
 * Position report + close/rollover — uses shared session/CSV/form/date helpers (utils/*).
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { api } from "../../utils/api";
import { downloadCsvFromObjects } from "utils/csvExport";
import { extractSelectOrPrimitive } from "utils/formValues";
import { safeParseInt } from "utils/numberParse";
import { getNormalizedThunkSession } from "utils/tradeSession";
import { formatApiDateMomentIso } from "utils/dateFormats";
import { axiosRejectPayload, legacyAxiosToastMessage } from "utils/httpErrors";
import { mapOpenPositionRecords, mergeLiveMtmIntoOpenPositions } from "utils/openPositionMapper";
import { getMarketWatchList } from "./marketwatch.slice";
import { showMessage } from "./common.slice";

function toastFromApi(dispatch, body, variant = "success") {
  const msg = body?.message ?? body?.Message;
  if (msg == null || String(msg).trim() === "") return;
  dispatch(showMessage({ message: String(msg), varient: variant }));
}

// ── initialState ──────────────────────────────────────────────────────────────
const initialState = {
  // Plain arrays — new UI does NOT use Maps
  positionReportList          : [],
  filteredPositionReportList  : [],
  duplicatePositionReportList : [],

  positionReportLoading       : false,
  totalRecordPositionReport   : 0,
  currentPagePositionReport   : 1,
  pageSizePositionReport      : 10000,

  positionReportFilterValue   : {},
  positionReportSearchText    : "",

  closePositonSelected        : [],

  positionCloseConfirmDialog  : false,
  positionCloseData           : {},
  isPositionCloseLoading      : false,
  isClosePosition             : false,

  liveRateFromSocket          : {},
  positionDisplayNames        : [],

  isPositionGet               : false,
};

// ─────────────────────────────────────────────────────────────────────────────
// getPositionReportList
// ─────────────────────────────────────────────────────────────────────────────
export const getPositionReportList = createAsyncThunk(
  "positionReport/get",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(data.userId || session.userId);
      const roles   = session.roles;

      const {
        searchText, pageNo, pageSize, appendData,
        disptype, clientcode, subbrokercode, crtype,
        segment, valan, symbol, reporttype,
        exdate, isExDateSel,
      } = data;

      // Valan: react-select option shape { label, value: { id, startDate, endDate } }
      const valanObj = extractSelectOrPrimitive(valan); // null or { id, startDate, endDate }

      const reportType = reporttype === "client" ? 1 : reporttype === "script" ? 2 : 3;

      const queryUserId = data.userId
        ? safeParseInt(data.userId)
        : reporttype === "self" && extractSelectOrPrimitive(subbrokercode)
          ? safeParseInt(extractSelectOrPrimitive(subbrokercode))
          : userId;

      let clientId = null;
      if (reporttype === "client") {
        clientId = clientcode && Object.keys(clientcode).length > 0
          ? safeParseInt(extractSelectOrPrimitive(clientcode))
          : roles[0] === "User" ? userId : null;
      } else if (reporttype === "self") {
        clientId = roles[0] === "User" ? userId : null;
      }

      let startDate, endDate;
      if (crtype === "day") {
        startDate = formatApiDateMomentIso(new Date());
        endDate   = formatApiDateMomentIso(new Date());
      } else {
        startDate = valanObj?.startDate ? formatApiDateMomentIso(valanObj.startDate) : formatApiDateMomentIso(new Date());
        endDate   = valanObj?.endDate   ? formatApiDateMomentIso(valanObj.endDate)   : formatApiDateMomentIso(new Date());
      }

      const isClosed = disptype === "all";

      // History Module Step 6 — Closed Positions use positions/get-closed
      if (isClosed) {
        const closedPayload = {
          userId    : queryUserId,
          pageNo    : safeParseInt(pageNo)   || 1,
          pageSize  : safeParseInt(pageSize) || 10000,
          segmentId : extractSelectOrPrimitive(segment) != null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
          clientId,
          symbol    : extractSelectOrPrimitive(symbol) ?? null,
          searchText: searchText || null,
          startDate,
          endDate,
        };
        const { data: closedRes } = await api.post("positions/get-closed", closedPayload);
        if (closedRes.status == 200) {
          const records = (closedRes.data?.records || []).map((item) => ({
            ...item,
            type         : item.type || "",
            lots         : item.volumeLots,
            qty          : item.volumeQty,
            openPrice    : item.priceOpen,
            currentPrice : item.priceClose,
            profit       : item.realizedPnl,
            script       : item.displayName || item.symbol,
            displayName  : item.displayName || item.symbol,
            mtm          : item.realizedPnl,
            ltp          : item.priceClose,
            orderType    : item.type,
          }));
          return {
            data: { ...closedRes.data, records, totalRecords: closedRes.data?.totalRecords || records.length },
            appendData,
          };
        }
        const failMsg = closedRes?.message ?? closedRes?.Message;
        if (failMsg != null && String(failMsg).trim() !== "") {
          dispatch(showMessage({ message: String(failMsg) }));
        }
        return rejectWithValue(closedRes);
      }

      const payload = {
        createdBy    : userId,
        reportType,
        userId       : queryUserId,
        pageNo       : safeParseInt(pageNo)   || 1,
        pageSize     : safeParseInt(pageSize) || 10000,
        isDayWise    : crtype === "day",
        isOutStanding: true,
        segmentId    : extractSelectOrPrimitive(segment) != null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
        startDate,
        endDate,
        valanId      : valanObj ? safeParseInt(valanObj.id) : 0,
        authorityId  : reporttype === "self" && extractSelectOrPrimitive(subbrokercode)
                         ? safeParseInt(extractSelectOrPrimitive(subbrokercode))
                         : roles[0] !== "Super Admin" && reporttype === "self"
                           ? userId : null,
        subBrokerId  : extractSelectOrPrimitive(subbrokercode) && reporttype !== "self"
                         ? safeParseInt(extractSelectOrPrimitive(subbrokercode)) : null,
        clientId,
        searchText   : searchText || null,
        exDate       : isExDateSel ? formatApiDateMomentIso(exdate) : null,
        symbol       : extractSelectOrPrimitive(symbol) ?? null,
      };

     

      const { data: res } = await api.post("position-report/get-open-positions", payload);

      if (res.status == 200) {
        // Do not toast on list fetch — it overwrites / clears the order-success toast
        // (generic API "Success" hits shouldSuppressGenericSuccessToast → showMessage=false).
        return { data: res.data, appendData };
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);

    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// exportPositionReport
// ─────────────────────────────────────────────────────────────────────────────
export const exportPositionReport = createAsyncThunk(
  "positionReport/export",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(data.userId || session.userId);

      const { disptype, clientcode, subbrokercode, crtype,
              segment, valan, symbol, reporttype, exdate, isExDateSel } = data;

      const valanObj = extractSelectOrPrimitive(valan);

      const payload = {
        userId,
        reportType   : reporttype === "client" ? 1 : reporttype === "script" ? 2 : 3,
        pageNo       : 1,
        pageSize     : 200000,
        isDayWise    : crtype === "day",
        isOutStanding: disptype !== "all",
        segmentId    : extractSelectOrPrimitive(segment) != null ? safeParseInt(extractSelectOrPrimitive(segment)) : null,
        startDate    : crtype === "day" ? formatApiDateMomentIso(new Date()) : (valanObj?.startDate ? formatApiDateMomentIso(valanObj.startDate) : formatApiDateMomentIso(new Date())),
        endDate      : crtype === "day" ? formatApiDateMomentIso(new Date()) : (valanObj?.endDate   ? formatApiDateMomentIso(valanObj.endDate)   : formatApiDateMomentIso(new Date())),
        valanId      : valanObj ? safeParseInt(valanObj.id) : 0,
        subBrokerId  : extractSelectOrPrimitive(subbrokercode) ? safeParseInt(extractSelectOrPrimitive(subbrokercode)) : null,
        clientId     : extractSelectOrPrimitive(clientcode)    ? safeParseInt(extractSelectOrPrimitive(clientcode))    : null,
        symbol       : extractSelectOrPrimitive(symbol) ?? null,
        searchText   : data.searchText || null,
        exDate       : isExDateSel ? formatApiDateMomentIso(exdate) : null,
      };

      const { data: res } = await api.post("position-report/get-open-positions", payload);

      if (res.status == 200) {
        toastFromApi(dispatch, res, "success");
        const rows = (res.data?.records || []).map(item => ({
          segment      : item.segment,
          client       : item.clientName,
          symbol       : item.symbol,
          script       : item.script,
          openTime     : item.openTime,
          type         : item.type,
          lots         : item.lots,
          qty          : item.qty,
          openPrice    : item.openPrice,
          currentPrice : item.currentPrice,
          profit       : item.profit,
          positionId   : item.positionId,
          authority    : item.authorityName,
        }));
        downloadCsvFromObjects(rows, "PositionReport");
        return { data: res.data };
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);
    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// closePosition
// ─────────────────────────────────────────────────────────────────────────────
export const closePosition = createAsyncThunk(
  "positionReport/closeOne",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(session.userId);

      const payload = {
        createdBy     : userId,
        userId        : safeParseInt(data.userId),
        id            : 0,
        deviceType    : 1,
        segmentId     : safeParseInt(data.segmentId),
        script        : data.script,
        displayName   : data.displayName,
        userIp        : session.userIp || "",
        orderType     : data.oldNetQty > 0 ? 2 : 1,
        lot           : Number(data.lot),
        qty           : data.segmentId !== 1
                          ? Number(data.oneQtySize) * Number(data.lot)
                          : Number(data.qty),
        rate          : Number(data.price),
        mediumPrice   : Number(data.ltp),
        lowPrice      : 0,
        highPrice     : 0,
        /* Must match fxbazzar_frontend positionReport.slice closePosition */
        executionType :
          data.marketType == "market"
            ? "market"
            : data.marketType == "limit" && data.oldNetQty > 0
              ? "sellLimit"
              : "buyLimit",
        symbol        : data.symbol,
        positionId    : data.positionId || null,
      };

      const { data: res } = await api.post("position-report/save-close-position", payload);

      if (res.status == 200) {
        toastFromApi(dispatch, res, "success");
        dispatch(getMarketWatchList({ isRefresh: true }));
        return res;
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);
    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// saveAllClosePosition
// ─────────────────────────────────────────────────────────────────────────────
export const saveAllClosePosition = createAsyncThunk(
  "positionReport/closeAll",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(session.userId);

      const finalArray = (data || []).map(item => ({
        deviceType    : 1,
        createdBy     : userId,
        userId        : safeParseInt(item.userId),
        id            : 0,
        segmentId     : safeParseInt(item.segmentId),
        script        : item.script,
        displayName   : item.displayName,
        userIp        : session.userIp || "",
        orderType     : item.netQty > 0 ? 2 : 1,
        lot           : Math.abs(Number(item.netLot)),
        qty           : Math.abs(Number(item.netQty)),
        rate          : Number(item.ltp),
        mediumPrice   : Number(item.ltp),
        lowPrice      : item.lowPrice  || 0,
        highPrice     : item.highPrice || 0,
        executionType : 5,
        symbol        : item.symbol,
        positionId    : item.positionId || null,
      }));

      const { data: res } = await api.post("position-report/save-all-close-position", finalArray);

      if (res.status == 200) {
        toastFromApi(dispatch, res, "success");
        dispatch(getMarketWatchList({ isRefresh: true }));
        return res;
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);
    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// rollOverPosition
// ─────────────────────────────────────────────────────────────────────────────
export const rollOverPosition = createAsyncThunk(
  "positionReport/rollover",
  async (passedSelected, { dispatch, getState, rejectWithValue }) => {
    try {
      const session = getNormalizedThunkSession();
      const userId  = safeParseInt(session.userId);

      // Accept directly-passed array OR fall back to Redux store
      const { closePositonSelected } = getState().positionReport;
      const selected = (passedSelected && passedSelected.length > 0)
        ? passedSelected
        : closePositonSelected;

      if (!selected || selected.length === 0) {
        dispatch(showMessage({ message: "Please select at least one record" }));
        return rejectWithValue("Please select at least one record");
      }

      const finalArray = selected.map(item => ({
        userId      : item.userId,
        subBrokerId : null,
        script      : item.script,
        segmentId   : item.segmentId,
        segmentCode : item.segmentCode,
        symbol      : item.symbol,
        token       : item.token,
        exDate      : moment(item.exDate).format("YYYY-MM-DD"),
        date        : moment().format(),
        displayName : item.displayName,
        userIp      : session.userIp || "",
        valanId     : 0,
        deviceType  : 1,
        orderType   : item.netQty > 0 ? 1 : 2,
        tradeOption : 1,
        tradeType   : 1,
        isCheckHL   : true,
        lot         : Math.abs(item.netLot),
        qty         : Math.abs(item.netQty),
        lowPrice    : 0,
        mediumPrice : 0,
        highPrice   : 0,
        createdBy   : userId,
      }));

      const { data: res } = await api.post("position-report/save-roll-over-position", finalArray);

      if (res.status == 200) {
        toastFromApi(dispatch, res, "success");
        return res;
      }
      const failMsg = res?.message ?? res?.Message;
      if (failMsg != null && String(failMsg).trim() !== "") {
        dispatch(showMessage({ message: String(failMsg) }));
      }
      return rejectWithValue(res);
    } catch (err) {
      dispatch(showMessage({ message: legacyAxiosToastMessage(err) }));
      return rejectWithValue(axiosRejectPayload(err));
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
export const positionReportSlice = createSlice({
  name: "positionReport",
  initialState,
  reducers: {
    setPositionReportSearchText    : (s, { payload }) => { s.positionReportSearchText   = payload; },
    setStartPagePositionReport     : (s, { payload }) => { s.currentPagePositionReport  = payload; },
    setTotalRecordPositionReport   : (s, { payload }) => { s.totalRecordPositionReport  = payload; },
    setPositionReportFilterValue   : (s, { payload }) => { s.positionReportFilterValue  = payload; },
    setClosePositonSelected        : (s, { payload }) => { s.closePositonSelected       = payload; },
    setIsPositionGet               : (s, { payload }) => { s.isPositionGet              = payload; },
    setPositionCloseConfirmDialog  : (s, { payload }) => { s.positionCloseConfirmDialog = payload; },
    setPositionCloseData           : (s, { payload }) => { s.positionCloseData          = payload; },
    setIsClosePosition             : (s, { payload }) => { s.isClosePosition            = payload; },
    setSocketData                  : (s, { payload }) => { s.liveRateFromSocket         = payload; },
    setPositionDisplayNames        : (s, { payload }) => { s.positionDisplayNames       = payload; },
    // Plain array setters — new UI uses arrays, NOT Maps
    setPositionReportList          : (s, { payload }) => { s.positionReportList          = payload || []; },
    setDuplicatePositionReportList : (s, { payload }) => { s.duplicatePositionReportList = payload || []; },
    setfilteredPositionReportList  : (s, { payload }) => { s.filteredPositionReportList  = payload || []; },
  },
  extraReducers: (builder) => {

    // ── getPositionReportList ────────────────────────────────────────────────
    builder
      .addCase(getPositionReportList.pending, (s) => {
        s.positionReportLoading = true;
      })
      .addCase(getPositionReportList.rejected, (s) => {
        s.positionReportList          = [];
        s.filteredPositionReportList  = [];
        s.duplicatePositionReportList = [];
        s.positionReportLoading       = false;
        s.totalRecordPositionReport   = 0;
        s.isPositionGet               = false;
      })
      .addCase(getPositionReportList.fulfilled, (s, { payload }) => {
        const records = payload.data?.records || [];
        const previousRows = s.filteredPositionReportList?.length
          ? s.filteredPositionReportList
          : s.positionReportList || [];

        let list;
        if (payload.appendData) {
          const existing = s.positionReportList || [];
          const newMapped = mapOpenPositionRecords(records, existing.length);
          list = [...existing, ...newMapped].map((r, idx) => ({
            ...r,
            isCheck: r.isCheck || false,
            rowId: idx + 1,
          }));
        } else {
          const mapped = mapOpenPositionRecords(records).map((r, idx) => ({
            ...r,
            isCheck: false,
            rowId: idx + 1,
          }));
          // Keep live MTM across auto-refresh so UI does not flash stale API profit/price.
          list = mergeLiveMtmIntoOpenPositions(mapped, previousRows);
        }

        s.positionReportList          = list;
        s.duplicatePositionReportList = list;
        s.filteredPositionReportList  = list;

        s.positionDisplayNames = list
          .filter(a => a.netQty !== 0)
          .map(a => a.displayName);

        s.totalRecordPositionReport = payload.data?.totalRecords || 0;
        s.positionReportLoading     = false;
        s.isPositionGet             = false;
      });

    // ── closePosition (single) ───────────────────────────────────────────────
    builder
      .addCase(closePosition.pending,   (s) => { s.isPositionCloseLoading = true;  s.isClosePosition = false; })
      .addCase(closePosition.rejected,  (s, { payload }) => {
        s.isPositionCloseLoading = false;
        // Stale UI row: server says already closed — refresh list to drop the row
        const msg = String(payload?.message || payload?.Message || "").toLowerCase();
        s.isClosePosition = msg.includes("position is close");
      })
      .addCase(closePosition.fulfilled, (s) => { s.isPositionCloseLoading = false; s.isClosePosition = true;  });

    // ── saveAllClosePosition ─────────────────────────────────────────────────
    builder
      .addCase(saveAllClosePosition.pending,   (s) => { s.isPositionCloseLoading = true;  s.isClosePosition = false; })
      .addCase(saveAllClosePosition.rejected,  (s, { payload }) => {
        s.isPositionCloseLoading = false;
        const msg = String(payload?.message || payload?.Message || "").toLowerCase();
        s.isClosePosition = msg.includes("position is close");
      })
      .addCase(saveAllClosePosition.fulfilled, (s) => { s.isPositionCloseLoading = false; s.isClosePosition = true;  });

    // ── rollOverPosition ─────────────────────────────────────────────────────
    builder
      .addCase(rollOverPosition.pending,   (s) => { s.isPositionCloseLoading = true;  s.isClosePosition = false; })
      .addCase(rollOverPosition.rejected,  (s) => { s.isPositionCloseLoading = false; s.isClosePosition = false; })
      .addCase(rollOverPosition.fulfilled, (s) => { s.isPositionCloseLoading = false; s.isClosePosition = true;  });

    // ── exportPositionReport ─────────────────────────────────────────────────
    builder
      .addCase(exportPositionReport.pending,   (s) => { s.positionReportLoading = true;  })
      .addCase(exportPositionReport.rejected,  (s) => { s.positionReportLoading = false; })
      .addCase(exportPositionReport.fulfilled, (s) => { s.positionReportLoading = false; });
  },
});

export const {
  setPositionReportSearchText,
  setStartPagePositionReport,
  setTotalRecordPositionReport,
  setPositionReportFilterValue,
  setClosePositonSelected,
  setIsPositionGet,
  setPositionCloseConfirmDialog,
  setPositionCloseData,
  setIsClosePosition,
  setSocketData,
  setPositionDisplayNames,
  setPositionReportList,
  setDuplicatePositionReportList,
  setfilteredPositionReportList,
} = positionReportSlice.actions;

export default positionReportSlice.reducer;